/**
 * The box map as a 3-D model: the voxels the wrist camera has mapped, coloured by height, and the
 * gripper's camera mount where it is now -- green, amber or red as the arm judges it against the
 * box -- with the path the arm checked it along and the point that came closest.
 *
 * The gripper end of the arm -- the last two links, the gripper and its fingers, with the camera
 * mount on it -- is drawn from the arm's own meshes once they have arrived (they come every 15 s),
 * each body where the status says it is. Only the camera mount is checked against the box, so only
 * it takes the warning's colour; the rest is grey.
 *
 * The taught path -- START, the waypoints, END -- is drawn in violet, and while the operator is
 * teaching, a fainter line runs from the last mark to the gripper (taughtPath.ts).
 *
 * One object for both places it is shown: the desktop page (BoxMapPanel) and the headset, beside
 * the picture (vr/scene.ts). It knows nothing of either, only of the map and the status.
 */
import { AmbientLight, Box3, BufferAttribute, BufferGeometry, Color, DirectionalLight, DoubleSide, GridHelper, Group, Line,
         LineBasicMaterial, Matrix4, Mesh, MeshBasicMaterial, MeshLambertMaterial, Points, PointsMaterial, SphereGeometry,
         Vector3 } from "three";

import type { BoxMap, ToolModel } from "../adamo/boxmap";
import type { Status } from "../adamo/types";
import { TaughtPath } from "./taughtPath";

const LEVEL_COLOUR = { clear: 0x3ddc84, near: 0xf5a623, hit: 0xff3b3b } as const;
const MAX_PATH = 64;

/** Height (m above the bench) to colour: deep blue on the floor, through green, to orange at the rim. */
const STOPS: [number, Color][] = [
  [0.0, new Color(0x1d2f7a)], [0.03, new Color(0x1f86a0)], [0.08, new Color(0x6fcf6a)],
  [0.13, new Color(0xf2d24b)], [0.17, new Color(0xff7a3d)],
];

function heightColour(z: number, out: Color): Color {
  if (z <= STOPS[0][0]) return out.copy(STOPS[0][1]);
  for (let i = 1; i < STOPS.length; i += 1) {
    const [z1, c1] = STOPS[i];
    if (z <= z1) {
      const [z0, c0] = STOPS[i - 1];
      return out.copy(c0).lerp(c1, (z - z0) / (z1 - z0));
    }
  }
  return out.copy(STOPS[STOPS.length - 1][1]);
}

export class BoxMapView {
  /** Add this to a scene. y up; the arm's base at the origin, reaching away along -z. */
  readonly group = new Group();
  /** The arm's base frame: x forward, y left, z up. */
  private readonly base = new Group();
  private readonly voxels: Points;
  private readonly tool = new Group();
  private readonly mount: Points;
  private readonly axis: Line;   // the gripper, until its meshes come
  private readonly contact: Mesh;
  private readonly path: Line;
  private readonly taught = new TaughtPath();
  /** Where the voxels are, base frame, less the outermost half percent on each side: a few stray
   *  voxels far out would otherwise decide how much room the whole map is given. */
  private readonly voxelBounds = new Box3();
  private level: keyof typeof LEVEL_COLOUR = "clear";
  private mountSet = false;
  /** The gripper end's bodies, each posed from the status; empty until the meshes have come. */
  private readonly bodies = new Map<string, Group>();
  private readonly camera = new MeshLambertMaterial({ color: LEVEL_COLOUR.clear, side: DoubleSide });
  private readonly steel = new MeshLambertMaterial({ color: 0x8e9aab, side: DoubleSide });
  private readonly pads = new MeshLambertMaterial({ color: 0xc9d2dc, side: DoubleSide });
  /** Where the box is, in `group` coordinates: the middle of what stands above the bench. */
  readonly focus = new Vector3(-0.05, 0.05, -0.32);

  constructor({ scale = 1, grid = false }: { scale?: number; grid?: boolean } = {}) {
    // arm x -> -z (away), y -> -x (left), z -> +y (up)
    this.base.setRotationFromMatrix(new Matrix4().set(0, -1, 0, 0, 0, 0, 1, 0, -1, 0, 0, 0, 0, 0, 0, 1));
    this.group.add(this.base);

    // Point sizes are in metres before the group's own scale, which three does not apply to them.
    this.voxels = new Points(new BufferGeometry(),
                             new PointsMaterial({ size: 0.006 * scale, vertexColors: true, sizeAttenuation: true }));
    this.voxels.frustumCulled = false;
    this.mount = new Points(new BufferGeometry(),
                            new PointsMaterial({ size: 0.004 * scale, color: LEVEL_COLOUR.clear, sizeAttenuation: true }));
    this.mount.frustumCulled = false;
    this.axis = new Line(new BufferGeometry().setFromPoints([new Vector3(0, 0, 0), new Vector3(0, 0, -0.15)]),
                         new LineBasicMaterial({ color: 0xe8ecf1 }));
    const tip = new Mesh(new SphereGeometry(0.005, 12, 8), new MeshBasicMaterial({ color: 0xe8ecf1 }));
    this.tool.add(this.mount, this.axis, tip);
    this.tool.visible = false;
    this.contact = new Mesh(new SphereGeometry(0.008, 16, 12), new MeshBasicMaterial({ color: LEVEL_COLOUR.hit }));
    this.contact.visible = false;
    const pathGeometry = new BufferGeometry();
    pathGeometry.setAttribute("position", new BufferAttribute(new Float32Array(MAX_PATH * 3), 3));
    pathGeometry.setDrawRange(0, 0);
    this.path = new Line(pathGeometry, new LineBasicMaterial({ color: LEVEL_COLOUR.clear }));
    this.path.frustumCulled = false;
    this.base.add(this.voxels, this.tool, this.contact, this.path, this.taught.group);
    // The voxels and lines are unlit; these light the gripper's meshes only.
    const sun = new DirectionalLight(0xffffff, 2.2);
    sun.position.set(0.4, 1.2, 0.9);
    this.group.add(new AmbientLight(0xffffff, 0.9), sun);
    if (grid) {
      const g = new GridHelper(1.2, 24, 0x2a323b, 0x1a2027);   // the bench, 5 cm squares, under the arm
      g.position.set(-0.0, 0, -0.3);
      this.group.add(g);
    }
  }

  /** A new map from the arm. */
  setMap(map: BoxMap): void {
    const g = this.voxels.geometry;
    const colours = new Float32Array(map.n * 3);
    const c = new Color();
    let sx = 0, sy = 0, k = 0;
    for (let i = 0; i < map.n; i += 1) {
      const z = map.positions[3 * i + 2];
      heightColour(z, c);
      colours[3 * i] = c.r; colours[3 * i + 1] = c.g; colours[3 * i + 2] = c.b;
      if (z > 0.03) { sx += map.positions[3 * i]; sy += map.positions[3 * i + 1]; k += 1; }
    }
    g.setAttribute("position", new BufferAttribute(map.positions, 3));
    g.setAttribute("color", new BufferAttribute(colours, 3));
    g.computeBoundingSphere();
    trimmedBounds(map.positions, map.n, 0.005, this.voxelBounds);
    if (k > 50) this.focus.set(-sy / k, 0.05, -sx / k);   // arm (x, y) of the box -> group coordinates
    if (!this.mountSet && map.mount.length) {
      this.mount.geometry.setAttribute("position", new BufferAttribute(map.mount, 3));
      this.mountSet = true;
    }
  }

  /** The gripper end's meshes, once: each part in its body's frame. */
  setToolModel(model: ToolModel): void {
    if (this.bodies.size) return;
    for (const part of model.parts) {
      const g = new BufferGeometry();
      g.setAttribute("position", new BufferAttribute(part.positions, 3));
      g.setIndex(new BufferAttribute(part.index, 1));
      g.computeVertexNormals();
      const material = part.role === "camera" ? this.camera : part.role === "finger" ? this.pads : this.steel;
      let body = this.bodies.get(part.body);
      if (!body) {
        body = new Group();
        body.visible = false;
        this.bodies.set(part.body, body);
        this.base.add(body);
      }
      body.add(new Mesh(g, material));
    }
    this.mount.visible = this.axis.visible = false;   // the gripper and its camera are solids now
  }

  /** The arm's status, as often as it comes: where the tool is, and what the rim guard makes of it. */
  setStatus(status: Status | null): void {
    const poses = status?.tool_poses ?? null;
    for (const [name, body] of this.bodies) {
      const p = poses?.[name];
      body.visible = Boolean(p);
      if (p) {
        body.position.set(p[0], p[1], p[2]);
        body.quaternion.set(p[3], p[4], p[5], p[6]);
      }
    }
    const ee = status?.ee, quat = status?.ee_quat;
    this.tool.visible = Boolean(ee && quat && this.mountSet);
    if (ee && quat) {
      this.tool.position.set(ee[0], ee[1], ee[2]);
      this.tool.quaternion.set(quat[0], quat[1], quat[2], quat[3]);   // both xyzw
    }
    const rim = status?.rim ?? null;
    const level = rim?.level ?? "clear";
    if (level !== this.level) {
      this.level = level;
      (this.mount.material as PointsMaterial).color.set(LEVEL_COLOUR[level]);
      this.camera.color.set(LEVEL_COLOUR[level]);
      (this.path.material as LineBasicMaterial).color.set(LEVEL_COLOUR[level]);
    }
    const contact = rim && rim.level !== "clear" ? rim.contact : null;
    this.contact.visible = Boolean(contact);
    if (contact) this.contact.position.set(contact[0] / 1000, contact[1] / 1000, contact[2] / 1000);
    const way = rim?.path ?? [];
    const attr = this.path.geometry.getAttribute("position") as BufferAttribute;
    const n = Math.min(way.length, MAX_PATH);
    for (let i = 0; i < n; i += 1) attr.setXYZ(i, way[i][0] / 1000, way[i][1] / 1000, way[i][2] / 1000);
    attr.needsUpdate = true;
    this.path.geometry.setDrawRange(0, n);
    this.taught.set(status?.taught, status?.state === "teach" ? status.ee : null);
  }

  /** How much room the model takes -- the voxels and the taught path, not the gripper, which moves --
   *  as a box in `group` coordinates (before the group's own position and scale). Empty if nothing. */
  extent(out: Box3): Box3 {
    this.taught.expand(out.copy(this.voxelBounds));
    if (out.isEmpty()) return out;
    this.base.updateMatrix();
    return out.applyMatrix4(this.base.matrix);   // a quarter-turn permutation: the box stays exact
  }

  /** Whether there is a taught path to draw: worth showing the model for, map or no map. */
  get hasPath(): boolean {
    return !this.taught.empty;
  }

  dispose(): void {
    this.voxels.geometry.dispose();
    (this.voxels.material as PointsMaterial).dispose();
    this.mount.geometry.dispose();
    (this.mount.material as PointsMaterial).dispose();
    this.path.geometry.dispose();
    for (const body of this.bodies.values()) {
      body.traverse((o) => { if (o instanceof Mesh) o.geometry.dispose(); });
    }
    this.camera.dispose(); this.steel.dispose(); this.pads.dispose();
    this.taught.dispose();
  }
}

/** The box holding all but `trim` of the points at each end of each axis. */
function trimmedBounds(positions: Float32Array, n: number, trim: number, out: Box3): Box3 {
  out.makeEmpty();
  if (n === 0) return out;
  const axis = new Float32Array(n);
  const lo = Math.floor(trim * (n - 1)), hi = Math.ceil((1 - trim) * (n - 1));
  for (let a = 0; a < 3; a += 1) {
    for (let i = 0; i < n; i += 1) axis[i] = positions[3 * i + a];
    axis.sort();
    out.min.setComponent(a, axis[lo]);
    out.max.setComponent(a, axis[hi]);
  }
  return out;
}
